import * as Sentry from "@sentry/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

import { useApi } from "@/api/hooks/useApi";
import { ConnectionFailurePage } from "@/components/Error/ConnectionFailurePage";
import { FALLBACK_UI_LANGUAGE, SUPPORTED_UI_LANGUAGES } from "@/helpers/languages";
import { LOCAL_STORAGE_KEYS } from "@/helpers/storage";
import { getLocalStorageValue, useUpdateLocalStorage } from "@/hooks/useLocalStorage";
import { QUERY_KEYS } from "@/query-keys";

import { I18nProvider } from "./implementation/I18nProvider";
import type { Languages, Translations } from "./implementation/types";

// Utility for showing translation keys in the UI for ease of debugging
const disableTranslations = getLocalStorageValue<boolean>("disableTranslations", false);
const browserLanguage = window.navigator.language.split("-")[0];

interface TranslationsProviderProps {
  children: React.ReactNode;
}

// Global provider for handling the retrieval and providance of POEditor translations based on known language setting saved in the user browser.
export function TranslationsProvider({ children }: TranslationsProviderProps): React.ReactNode {
  // Fallback priority for UI language:
  // 1) Local storage value
  // 2) Browser language
  // 3) Fallback language (English)
  const defaultLanguage = SUPPORTED_UI_LANGUAGES.some((lang) => lang === browserLanguage)
    ? (browserLanguage as Languages)
    : FALLBACK_UI_LANGUAGE;
  const knownPreferredLanguage = getLocalStorageValue<string>(LOCAL_STORAGE_KEYS.USER_SELECTED_LANGUAGE, "");
  // @ts-expect-error: Local storage can contain any string,
  const isKnownPreferredLanguageSupported = SUPPORTED_UI_LANGUAGES.includes(knownPreferredLanguage);
  const [language, setLanguage] = useState(
    isKnownPreferredLanguageSupported ? knownPreferredLanguage : defaultLanguage,
  );

  const api = useApi();

  useUpdateLocalStorage(LOCAL_STORAGE_KEYS.USER_SELECTED_LANGUAGE, language);
  useEffect(() => {
    document.documentElement.setAttribute("lang", language);
  }, [language]);

  const {
    data: translations,
    isPending: isPendingTranslations,
    isError: isErrorTranslations,
  } = useQuery({
    queryKey: QUERY_KEYS.TRANSLATIONS(language),
    queryFn: async () => {
      if (disableTranslations) {
        return {};
      }

      const result = await api.getTranslationsDetailsV1("dashboard", language as any);

      // Load local translations in development on initial load
      if (import.meta.env.DEV) {
        const devTranslations = await import("./english.json");
        const { parseTranslations } = await import("@/translations/implementation/parseTranslations");
        const devData = parseTranslations(devTranslations.default);
        result.data = { ...devData, ...result.data };
      }

      return result.data;
    },
    retry: 3,
    retryDelay: (x) => x * 1000,
  });

  // As we import the dev translation dynamically in a query, it can't be automatically hot reloaded
  // in development. We need to manually accept the module and update the query data.
  if (import.meta.env.DEV && import.meta.hot) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const queryClient = useQueryClient();
    import.meta.hot.accept("./english.json", async (mod) => {
      if (!mod) {
        console.error("Failed to load dev translations");

        return;
      }

      const result = await api.getTranslationsDetailsV1("dashboard", language as any);
      const { parseTranslations } = await import("@/translations/implementation/parseTranslations");
      const devData = parseTranslations(mod.default);

      queryClient.setQueryData(QUERY_KEYS.TRANSLATIONS(language), { ...devData, ...result.data });
    });
  }

  const handleChangeLanguage = useCallback(
    (newLanguage: string) => {
      if (newLanguage !== language) {
        setLanguage(newLanguage);
        // Easiest way to reload all user generated content to get the correct translations
        location.reload();
      }
    },
    [language],
  );

  const missingKeyHandler = useCallback((key: string, languageId: string) => {
    if (!disableTranslations) {
      Sentry.captureException(`Missing translation for ${languageId}: ${key}`);
    }
  }, []);

  const missingVariableHandler = useCallback((key: string, value: string, languageId: string) => {
    if (!disableTranslations) {
      Sentry.captureException(`Missing variable in ${value} in translation for ${languageId}: ${key}`);
    }
  }, []);

  if (isPendingTranslations) {
    return null;
  }

  if (isErrorTranslations) {
    return <ConnectionFailurePage poEditorCode={language} />;
  }

  return (
    <I18nProvider
      language={language}
      translations={translations as Translations}
      onChangeLanguage={handleChangeLanguage}
      onMissingKey={missingKeyHandler}
      onMissingVariable={missingVariableHandler}
    >
      {children}
    </I18nProvider>
  );
}
